<script setup>

    import { computed, ref, watch, nextTick } from 'vue'
    import LatteClientCriteriaList from '@/Components/Client/LatteClientCriteriaList.vue'
    import LatteNote from '@/Components/LatteNote.vue'

    const props = defineProps({
        caregiverStore: {
            type: Object,
            required: true,
        },
        matchingWowCriteria: {
            type: Array,
            default: () => [],
        },
        matchingMustHaveCriteria: {
            type: Array,
            default: () => [],
        },
        missingCriteria: {
            type: Array,
            default: () => [],
        }
    })

    const minHourlyRateText = computed(() => {
        return props.caregiverStore.caregiver.availability_pref_min_hourly_rate
            ? `$${props.caregiverStore.caregiver.availability_pref_min_hourly_rate} / hr`
            : '— / hr';
    })

    const minDailyRateText = computed(() => {
        return props.caregiverStore.caregiver.availability_pref_min_daily_rate
            ? `$${props.caregiverStore.caregiver.availability_pref_min_daily_rate} / day`
            : '— / day';
    })

    const noteLoading = ref(false)
    const noteSaving = ref(false)
    const noteEnd = ref(false)
    let offset = 10

    watch(() => props.caregiverStore.caregiver, (newCaregiver) => {
        offset = newCaregiver.latest_notes.length
    }, { immediate: true, deep: true })

    const newNoteText = ref('');
    const notesListRef = ref(null);

    function onNotesScroll(event) {
        const el = event.target;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight) {
            if (noteLoading.value || noteEnd.value) {
                return;
            }
            loadCaregiverNotes()
        }
    }

    const noteError = ref(null);

    watch(newNoteText, () => {
        noteError.value = null;
    })

    /**
     * The first validation message of a 422 response, shown under the composer; other errors only toast.
     */
    function validationMessage(err) {
        const response = err?.error?.response;
        if (response?.status !== 422) {
            return null;
        }
        const firstFieldErrors = Object.values(response.data?.errors ?? {})[0];

        return (Array.isArray(firstFieldErrors) ? firstFieldErrors[0] : firstFieldErrors) || response.data?.message || 'The note could not be saved.';
    }

    function addCaregiverNote() {
        if (newNoteText.value.trim()) {
            noteSaving.value = true;
            noteError.value = null;
            props.caregiverStore.addNote(newNoteText.value.trim())
                .then(() => {
                    newNoteText.value = '';
                    nextTick(() => {
                        if (notesListRef.value) {
                            notesListRef.value.scrollTop = 0;
                        }
                    });
                })
                .catch((err) => {
                    noteError.value = validationMessage(err);
                })
                .finally(() => {
                    noteSaving.value = false;
                })
        }
    }

    function loadCaregiverNotes() {
        if (noteEnd.value) {
            return;
        }

        noteLoading.value = true
        props.caregiverStore.retrieveNotes(offset, 10, 1)
            .then((newNotes) => {
                if (newNotes.length < 10) {
                    noteEnd.value = true
                }
            })
            .finally(() => {
                noteLoading.value = false
            })
    }

</script>

<template>

    <div class="grid grid-cols-2 px-4 gap-2 bg-white border border-lifeworx-blue-800 relative">

        <div class="col-span-1 flex flex-col items-start py-6 px-4 gap-8">

            <div class="flex items-center gap-4 text-xs text-lifeworx-blue-800 font-bold">
                <span>Min Rates:</span>
                <span class="text-sm">{{ minHourlyRateText }}</span>
                <span class="text-sm">{{ minDailyRateText }}</span>
            </div>

            <div class="text-xs text-lifeworx-blue-800 font-bold">
                <span>Matching Criteria</span>
                <div
                    v-if="matchingWowCriteria.length > 0 || matchingMustHaveCriteria.length > 0"
                    class="mt-2 flex flex-wrap gap-y-2 gap-x-1.5 "
                >
                    <LatteClientCriteriaList
                        v-if="matchingMustHaveCriteria.length > 0"
                        :criterias="matchingMustHaveCriteria"
                        type="searchMatchMustHave"
                        :loading="false"
                    />
                    <LatteClientCriteriaList
                        v-if="matchingWowCriteria.length > 0"
                        :criterias="matchingWowCriteria"
                        type="searchMatch"
                        :loading="false"
                    />
                </div>
                <div
                    v-else
                    class="mt-4 mb-10"
                >
                    <span class="italic text-base text-stone-400 font-semibold">No Matching Criteria</span>
                </div>
            </div>
            <div class="text-xs text-lifeworx-blue-800 font-bold">
                <span class="mb-2">Missing Criteria</span>
                <div
                    v-if="missingCriteria.length > 0"
                    class="mt-2"
                >
                    <LatteClientCriteriaList
                        :criterias="missingCriteria"
                        type="searchMissing"
                        :loading="false"
                    />
                </div>
                <div
                    v-else
                    class="mt-4"
                >
                    <span class="italic text-base text-stone-400 font-semibold">No Missing Criteria</span>
                </div>
            </div>
        </div>

        <div class="col-start-2 flex flex-col relative h-96">
            <div class="flex items-center gap-2 my-4 flex-shrink-0">
                <textarea
                    v-model="newNoteText"
                    rows="2"
                    class="flex-1 resize-y rounded-lg border border-stone-300 px-3 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-lifeworx-blue-400 placeholder:italic placeholder:text-stone-400 min-h-16 max-h-64"
                    :class="{ 'italic': !newNoteText }"
                    placeholder="Add a new note…"
                ></textarea>
                <button
                    class="rounded bg-lifeworx-blue-500 text-white w-10 h-12 flex items-center justify-center text-2xl font-bold shadow hover:bg-lifeworx-blue-600 transition cursor-pointer"
                    @click="addCaregiverNote"
                    :disabled="!newNoteText.trim()"
                    aria-label="Add Note"
                >+</button>
            </div>
            <p
                v-if="noteError"
                class="-mt-2 mb-2 text-sm font-semibold text-lifeworx-red-500"
                role="alert"
                data-test="note-error"
            >
                {{ noteError }}
            </p>
            <div
                v-if="noteSaving"
                class="flex items-center justify-center h-20"
            >
                <span class="mr-4 text-lg font-semibold">Saving New Note…</span>
                <div class="h-6 w-6 border-2 border-t-transparent border-t-lifeworx-blue-600 rounded-full animate-spin"></div>
            </div>
            <div v-else class="flex-1 min-h-0 overflow-y-auto pl-2" @scroll="onNotesScroll" ref="notesListRef">
                <ol class="relative">
                    <div class="absolute inset-y-0 w-0.5 -translate-x-px bg-[#c0e3ee]"></div>
                    <div class="relative py-4">
                        <template v-for="(note, idx) in caregiverStore.caregiver.latest_notes" :key="`${note.source}:${note.id}`">
                            <li
                                class="relative items-start mb-4"
                                :class="{ 'last:mb-12': !noteLoading, 'last:mb-4': noteLoading }"
                            >
                                <span
                                    class="absolute left-0 top-2 w-3 h-3 rounded-full translate-x-[-50%] translate-y-[-50%] border border-[#ebf8fa]"
                                    :class="{
                                        'bg-lifeworx-blue-600': note.creator,
                                        'bg-[#c0e3ee]': !note.creator
                                    }"
                                />
                                <div class="ml-4">
                                    <LatteNote :note="note"/>
                                </div>
                            </li>
                        </template>
                        <li v-if="caregiverStore.caregiver.latest_notes.length === 0 && !noteLoading" class="relative items-start mb-12 h-48">
                            <span class="absolute left-0 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[#c0e3ee] translate-x-[-50%] border border-[#ebf8fa]"></span>
                            <div class="ml-8 text-stone-400 italic font-semibold text-base flex items-center h-full">
                                No Notes Found
                            </div>
                        </li>
                    </div>
                </ol>
                <span v-if="noteLoading" class="flex items-center justify-center font-semibold text-lifeworx-blue-800 pb-8">
                    Loading more notes…
                    <div class="h-6 w-6 border-2 border-t-transparent border-t-lifeworx-blue-600 rounded-full animate-spin ml-4"></div>
                </span>
            </div>
            <div class="pointer-events-none absolute left-0 right-0 bottom-0 h-20 bg-gradient-to-t from-white to-transparent"></div>
        </div>
    </div>

</template>
